import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LinkButton } from "@/components/ui/link-button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Copy, Play, Users, CheckCircle, Heart, Download, MessageCircle } from "lucide-react";
import { getComplexityColor } from "@/lib/data.ts";
import type { WorkflowData } from '@/lib/types';

export function WorkflowPreviewDialog({
  previewWorkflow,
  setPreviewWorkflow,
  handleClone,
  handleRunDemo,
}: {
  previewWorkflow: WorkflowData | null;
  setPreviewWorkflow: (workflow: WorkflowData | null) => void;
  handleClone: (workflow: WorkflowData) => void;
  handleRunDemo: (workflow: WorkflowData) => void;
}) {
  return (
    <Dialog open={!!previewWorkflow} onOpenChange={() => setPreviewWorkflow(null)}>
      <DialogContent className="w-[95vw] max-w-[95vw] h-[90vh] md:max-h-[720px] p-0 md:w-[75vw] md:max-w-[1200px] md:h-[80vh] overflow-y-auto">
        {previewWorkflow && (
          <div className="flex flex-col md:flex-row h-full">
            {/* Mobile: Info First, Desktop: Canvas First */}

            {/* Mobile Header - Shows first on mobile */}
            <div className="block md:hidden border-b border-slate-200 dark:border-slate-700 p-4">
              <div className="space-y-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                    <Link
                      to={`/gallery/workflow/${previewWorkflow.id}`}
                      className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {previewWorkflow.title}
                    </Link>
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{previewWorkflow.category}</p>
                </div>

                {/* Author info */}
                <div className="flex items-center space-x-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage
                      src={previewWorkflow.author.avatar || "/placeholder.svg"}
                      alt={previewWorkflow.author.name}
                    />
                    <AvatarFallback>
                      {previewWorkflow.author.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-slate-900 dark:text-white">
                        {previewWorkflow.author.name}
                      </span>
                      {previewWorkflow.author.verified && <CheckCircle className="w-4 h-4 text-blue-500" />}
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{previewWorkflow.author.role}</p>
                  </div>
                </div>

                {/* Quick stats */}
                <div className="flex items-center justify-between">
                  <Badge className={getComplexityColor(previewWorkflow.complexity)}>
                    {previewWorkflow.complexity}
                  </Badge>
                  <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-slate-400">
                    <Link
                      to={`/gallery/workflow/${previewWorkflow.id}#comments`}
                      className="flex items-center hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <MessageCircle className="w-4 h-4 mr-1" />
                      {previewWorkflow.comments?.length || 0}
                    </Link>
                    <span className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      {previewWorkflow.uses.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2">
                  <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    onClick={() => handleClone(previewWorkflow)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Clone Workflow
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 bg-transparent"
                    onClick={() => handleRunDemo(previewWorkflow)}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Run Demo
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm">
                      <Heart className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                    <LinkButton
                      to={`/gallery/workflow/${previewWorkflow.id}`}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Share
                    </LinkButton>
                  </div>
                </div>
              </div>
            </div>

            {/* Canvas Area - Second on mobile, first on desktop */}
            <div className="flex-1 p-4 md:p-6 overflow-y-auto order-2 md:order-1">
              <div className="w-full h-64 md:h-full bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-300 dark:border-slate-600">
                <div className="text-center">
                  <div className="w-16 h-16 bg-slate-200 dark:bg-slate-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                      {(() => {
                        const Icon = previewWorkflow.icon as React.ElementType | undefined;
                        return Icon ? <Icon className="w-8 h-8 text-slate-500" /> : null;
                      })()}
                    </div>
                  <p className="text-slate-500 dark:text-slate-400 font-medium">Workflow Canvas</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                    Interactive workflow visualization would appear here
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop Sidebar - Hidden on mobile, shows on desktop */}
            <div className="hidden md:block w-96 border-l border-slate-200 dark:border-slate-700 p-6 overflow-y-auto order-3 md:order-2">
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                    <Link
                      to={`/gallery/workflow/${previewWorkflow.id}`}
                      className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {previewWorkflow.title}
                    </Link>
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{previewWorkflow.category}</p>
                </div>

                {/* Author info */}
                <div className="flex items-center space-x-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage
                      src={previewWorkflow.author.avatar || "/placeholder.svg"}
                      alt={previewWorkflow.author.name}
                    />
                    <AvatarFallback>
                      {previewWorkflow.author.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-slate-900 dark:text-white">
                        {previewWorkflow.author.name}
                      </span>
                      {previewWorkflow.author.verified && <CheckCircle className="w-4 h-4 text-blue-500" />}
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{previewWorkflow.author.role}</p>
                  </div>
                </div>

                {/* Complexity badge */}
                <div>
                  <Badge className={getComplexityColor(previewWorkflow.complexity)}>
                    {previewWorkflow.complexity}
                  </Badge>
                </div>

                {/* Description */}
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Description</h3>
                  <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                    {previewWorkflow.description}
                  </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <Link
                    to={`/gallery/workflow/${previewWorkflow.id}#comments`}
                    className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-center mb-1">
                      <MessageCircle className="w-4 h-4 text-slate-500 mr-1" />
                    </div>
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">{previewWorkflow.comments?.length || 0}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Comments</p>
                  </Link>
                  <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center justify-center mb-1">
                      <Users className="w-4 h-4 text-slate-500 mr-1" />
                    </div>
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">
                      {previewWorkflow.uses.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Uses</p>
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-1">
                    {previewWorkflow.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="space-y-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => handleClone(previewWorkflow)}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Clone Workflow
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full bg-transparent"
                    onClick={() => handleRunDemo(previewWorkflow)}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Run Demo
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm">
                      <Heart className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                    <LinkButton
                      to={`/gallery/workflow/${previewWorkflow.id}`}
                      variant="outline"
                      size="sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Share
                    </LinkButton>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Details Section - Shows after canvas on mobile */}
            <div className="block md:hidden border-t border-slate-200 dark:border-slate-700 p-4 order-3">
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Description</h3>
                  <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                    {previewWorkflow.description}
                  </p>
                </div>

                {/* Tags */}
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-1">
                    {previewWorkflow.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Detailed Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <Link
                    to={`/gallery/workflow/${previewWorkflow.id}#comments`}
                    className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center justify-center mb-1">
                      <MessageCircle className="w-4 h-4 text-slate-500 mr-1" />
                    </div>
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">{previewWorkflow.comments?.length || 0}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Comments</p>
                  </Link>
                  <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center justify-center mb-1">
                      <Users className="w-4 h-4 text-slate-500 mr-1" />
                    </div>
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">
                      {previewWorkflow.uses.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Uses</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
